import "./LinkPageCard.scss";
import {useState, useEffect, MouseEvent, useRef} from "react";
import useFocus from "../../hooks/useFocus";
import { LinkPageDto } from "../../domain/LinkPageDto";
import { env } from "../../env/env";
import { format } from "date-fns";
import { ellipsis } from "../../util/utils";
import EditDesignIcon from "../icons/EditDesignIcon";
import { Button, Dropdown, Input, Drawer } from "antd";
import PreviewIcon from "../icons/PreviewIcon";
import { MoreOutlined, EditOutlined, LinkOutlined, DownOutlined } from "@ant-design/icons";
import DeleteDesignIcon from "../icons/DeleteDesignIcon";
import ChangeLocationIcon from "../icons/ChangeLocationIcon";
import LinkPagesApi, {getLpBasePath} from "../../api/LinkPagesApi";
import { locationsState, memberState } from "../../state/atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import {LocationDto, MemberDtoPlanEnum} from "../../generated/dashboard-client";
import Storage from "../../util/Storage";
import { isProdEnv } from "../../util/constants";

export interface LinkPageCardProps {
  page: LinkPageDto;
  activeLocation: LocationDto;
  onDeleteClick: (page: LinkPageDto) => void;
  onChangeLocationClick: (page: LinkPageDto) => void;
}

export default function LinkPageCard({
  page,
  activeLocation,
  onDeleteClick,
  onChangeLocationClick,
}: LinkPageCardProps) {
  const dropdownButton = useRef<HTMLButtonElement>(null);
  const [renameInputRef, setRenameInputFocus] = useFocus();
  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [locations] = useRecoilState(locationsState);
  const member = useRecoilValue(memberState);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isLinkJustCopied, setLinkJustCopied] = useState(false);

  useEffect(() => {
    setName(page.name ?? "");
  }, [page]);

  function configPage() {
    window.location.href = `${getLpBasePath()}${env.lpContextPath}${isProdEnv ? '/lp' : ''}/app/${page.id}/links?businessId=${activeLocation.internalId}&tkn=${Storage.getToken()}`;
  }

  function preview() {
    window.open(`${getLpBasePath()}${env.lpContextPath}/lp/${page.id}`, "_blank");
  }

  function rename() {
    setRenaming(false);
    if (!!name) {
      LinkPagesApi.renameLinkPage(page.id, name)
        .then(() => {
          setName(name);
        })
        .catch((error) => {
          console.log("error renaming link page: " + error);
          setName(page.name);
        });
    }
  }

  function startRenaming() {
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  function changeLocation() {
    onChangeLocationClick(page);
  }

  function copyLink(e: MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(`${getLpBasePath()}${env.lpContextPath}/lp/${page.customId ? page.customId : page.id}`);
    setLinkJustCopied(true);

    setTimeout(() => {
      setLinkJustCopied(false)
    }, 3000)
  }

  function onCloseMobileMenu() {
    setMobileMenuVisible(false);
  }

  function hideDropdown() {
    dropdownButton.current?.click();
  }

  const menu = (
    <div className={"ops-menu link-page-ops"} onClick={hideDropdown}>
      <header className="mobile-display-name">
        <h4>{page.name}</h4>
      </header>
      <ul>
        <li>
          <Button type={"link"} className={"preview"} onClick={preview}>
            <PreviewIcon /> Preview
          </Button>
        </li>
        <li>
          <Button type={"link"} onClick={configPage}>
            <EditDesignIcon /> Edit
          </Button>
        </li>
        {member.plan !== MemberDtoPlanEnum.Free && (
          <li>
            <Button type={"link"} onClick={() => onDeleteClick(page)}>
              <DeleteDesignIcon /> Delete
            </Button>
          </li>
        )}
        {locations.length > 1 && (
          <li>
            <Button type={"link"} onClick={changeLocation}>
              <ChangeLocationIcon /> Change Location
            </Button>
          </li>
        )}
        <li className={"sep"} />
        <li>
          <Button type={"link"} onClick={copyLink}>
            <LinkOutlined />
            {isLinkJustCopied && <span>Copied!</span>}
            {!isLinkJustCopied && <span>Copy Link</span>}
          </Button>
        </li>
      </ul>
    </div>
  );

  return (
    <li className={"link-page"} key={page.id}>
      <Dropdown className={"more-btn-ops desktop-lp-menu"} overlay={menu} trigger={["click"]}>
        <MoreOutlined ref={dropdownButton} />
      </Dropdown>


      <div className={"mobile-lp-menu more-btn-ops"}>
        <MoreOutlined onClick={() => { setMobileMenuVisible(!isMobileMenuVisible) }} />
        <Drawer
          placement={"bottom"}
          visible={isMobileMenuVisible}
          onClose={onCloseMobileMenu}
          className={"mobile-bottom-menu"}
          closeIcon={<DownOutlined/>}
          height={"320px"}>
          {menu}
        </Drawer>
      </div>

      <div className={"wrap"} onClick={configPage}>
        <iframe
          title={page.id}
          src={`${getLpBasePath()}${env.lpContextPath}/lp/${page.id}?preview=true`}
        />
      </div>
      <section>
        {!isRenaming && (
          <div className={"edit-name"} onClick={startRenaming}>
            <h3>{name || page.name}</h3>
            <Button type={"link"}>
              <EditOutlined />
            </Button>
          </div>
        )}
        <div className={`editing-name ${isRenaming ? "show" : ""}`}>
          <Input
            ref={renameInputRef}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            onBlur={rename}
          />
        </div>

        <ul>
          <li>
            <label>Link:</label>
            <span>
              <a
                href={`${getLpBasePath()}${env.lpContextPath}/lp/${page.customId ? page.customId : page.id}`}
                target="_blank"
                rel="noreferrer"
              >
                {ellipsis(`${getLpBasePath()}${env.lpContextPath}/lp/${page.customId ? page.customId : page.id}`)}
              </a>
            </span>
          </li>
          <li>
            <label>Created:</label>
            <span>{format(new Date(page.createdDate), "EEE LLL dd yyyy")}</span>
          </li>
          <li>
            <label>Last Published:</label>
            <span>{format(new Date(page.updatedDate), "EEE LLL dd yyyy")}</span>
          </li>
        </ul>

        <footer>
          <label>{page.viewCount} views</label>
        </footer>
      </section>
    </li>
  );
}
